import { ReactElement, useEffect } from 'react'
import { useQuery, useMutation, NetworkStatus } from '@apollo/client'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'
import { useNavigate, useParams } from 'react-router-dom'
import styled from 'styled-components'
import { lowerCase } from 'lodash'

import Heading from 'components/v2/Heading'
import { Button } from 'components/v2/Button'
import EmptyState from 'components/v2/EmptyState'
import Card from '@10xLabs/react-web/components/Card'
import { Loader } from '@10xLabs/react-web/components/Loader'
import { INewCouponTemplateForm } from 'features/Campaigns/components/NewCouponTemplate/types'
import FormLayout from 'features/App/components/FormLayout'
import { NewCouponTemplate } from 'features/Campaigns/components/NewCouponTemplate'
import { ForbiddenView } from 'features/App/components/ForbiddenView'
import { withLastUrl } from 'features/App/components/WithLastUrl'
import { FEATURES } from 'features/Roles/components/Permissions/constants'
import { GTCampaignType, GTConfirmationModality } from 'gql/types.generated'
import { SET_COUPON_TEMPLATE, GET_CAMPAIGN_COUPON_TEMPLATE } from 'features/Campaigns/queries-legacy'
import { canCreateSubcampaign, canRead, canWrite } from 'features/Users/utils'
import { useLogin } from 'features/Login/hooks'
import { CREATE_CAMPAIGN_SUBCAMPAIGN } from 'features/Trips/mutations-legacy'

const FormLayoutContent = styled(FormLayout.Content)`
  margin-bottom: 200px;
`

const messages = defineMessages({
  helmet: {
    id: 'coupons.templates.new.helmet',
    defaultMessage: 'New Coupon Template | Nexbus Admin'
  }
})

const NewCouponTemplateView = (): ReactElement => {
  let content
  const navigate = useNavigate()
  const { campaignId } = useParams()
  const intl = useIntl()
  const login = useLogin()
  const canModifyTemplate = canWrite(FEATURES.CAMPAIGN, login.accessToken.policies)
  const canReadTemplate = canRead(FEATURES.CAMPAIGN, login.accessToken.policies)
  const canCreateDBSubcampaign = canCreateSubcampaign(login.accessToken.policies)

  const {
    loading: queryLoading,
    error: queryError,
    data: queryData,
    refetch,
    networkStatus
  } = useQuery(GET_CAMPAIGN_COUPON_TEMPLATE, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    skip: !campaignId || !canReadTemplate,
    variables: {
      campaignId
    }
  })

  const [setCouponTemplate, { loading: mutationLoading, error: mutationError }] = useMutation(SET_COUPON_TEMPLATE)
  const [createSubcampaign, { loading: loadingCreateSubcampaign, error: errorCreateSubcampaign }] =
    useMutation(CREATE_CAMPAIGN_SUBCAMPAIGN)

  const handleSubmit = async (template: INewCouponTemplateForm): Promise<void> => {
    const payload = {
      id: template.campaign.id,
      prefix: template.prefix,
      value: Number(template.value),
      salesChannelIDs: template.salesChannels.map(({ id }) => id),
      expirationTime: {
        unit: template.expirationTime.unit,
        value: Number(template.expirationTime.value)
      },
      confirmationModalities: {
        [lowerCase(GTConfirmationModality.Paid)]: template.confirmationModalities.includes(GTConfirmationModality.Paid),
        [lowerCase(GTConfirmationModality.Reserved)]: template.confirmationModalities.includes(
          GTConfirmationModality.Reserved
        )
      }
    }
    if (canModifyTemplate) {
      await setCouponTemplate({
        variables: {
          template: payload
        }
      })
    }

    if (template.campaign.type === GTCampaignType.UniqueCouponForDatabase && canCreateDBSubcampaign) {
      await createSubcampaign({
        variables: {
          input: {
            id: template.campaign.id,
            filePath: template.filePath,
            name: template.subcampaignName
          }
        }
      })
    }
    navigate('/campaigns')
  }

  useEffect(() => {
    document.title = intl.formatMessage(messages.helmet)
  }, [])

  if (!canReadTemplate) {
    return <ForbiddenView feature={FEATURES.CAMPAIGN} />
  }
  if (networkStatus === NetworkStatus.refetch || queryLoading) {
    content = (
      <Card.Content noHeader noFooter>
        <EmptyState.Container>
          <Loader />
        </EmptyState.Container>
      </Card.Content>
    )
  } else if (queryError || !queryData?.campaign) {
    content = (
      <Card.Content noHeader noFooter>
        <EmptyState.Container>
          <EmptyState.Description>
            <FormattedMessage
              id="coupons.templates.new.error.campaign.description"
              defaultMessage="There was an error while loading your campaign"
            />
          </EmptyState.Description>
          <EmptyState.Action>
            <Button
              onClick={(): void => {
                refetch()
              }}
              kind="primary"
            >
              <FormattedMessage
                id="coupons.templates.new.error.campaign.action"
                defaultMessage="Reload your campaign"
              />
            </Button>
          </EmptyState.Action>
        </EmptyState.Container>
      </Card.Content>
    )
  } else {
    content = (
      <NewCouponTemplate
        onSubmit={handleSubmit}
        loading={mutationLoading || loadingCreateSubcampaign}
        error={mutationError || errorCreateSubcampaign}
        campaign={queryData.campaign}
        isEdit={queryData.campaign.template}
        canCreateDBSubcampaign={canCreateDBSubcampaign}
        canModifyTemplate={canModifyTemplate}
      />
    )
  }

  return (
    <div>
      <FormLayout.Header>
        <Heading.H3>
          <FormattedMessage id="coupons.template.new.title" defaultMessage="Coupon Template" />
        </Heading.H3>
      </FormLayout.Header>
      <FormLayoutContent>{content}</FormLayoutContent>
    </div>
  )
}

export default withLastUrl(NewCouponTemplateView)
